<script generic="T extends DataTableProps['value']" lang="ts" setup>
import type { DataTableFilterMetaData, DataTableProps } from 'primevue/datatable';
import VTransitionGroup from '@shared/animation/VTransitionGroup/VTransitionGroup.vue';
import VPopover from '@shared/component/VPopover/VPopover.vue';
import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import type { ActiveFilters, Property, ValueColors } from './types';
import VDataTableFiltersButton from './VDataTableFiltersButton.vue';

const props = defineProps<{
  properties: Array<Property<keyof T>>;
  values: T;
  valueColors?: ValueColors<T>;
}>();

const model = defineModel<Record<keyof T, DataTableFilterMetaData>>({ default: {} });

const popover = ref<InstanceType<typeof VPopover>>();

const activeFilters: ComputedRef = computed(() =>
  Object.entries(model.value)
    .filter(([field]) => field !== 'global')
    .reduce(
      (previousValue, [field, filterValue]) => [
        ...previousValue,
        ...(filterValue as DataTableFilterMetaData).value.map((value: any) => ({
          field,
          value,
        })),
      ],
      [] as ActiveFilters<keyof T>[],
    ),
);

const filters = computed(() =>
  props.properties
    .map((property) => ({
      property,
      values: Array.from(new Set(props.values?.map((value) => value[property.field]))).sort(),
    }))
    .filter(({ values }) => values.length > 1),
);

function addModelValue(field: keyof T, value: string) {
  model.value = {
    ...model.value,
    [field]: { matchMode: 'in', value: [...(model.value[field]?.value || []), value] },
  };
}

const getFilterActive = (field: keyof T, value: string) =>
  field in model.value && model.value[field].value.includes(value);

const getValueColor = (field: keyof T, value: string) =>
  props.valueColors && field in props.valueColors && value in props.valueColors[field]
    ? props.valueColors[field][value]
    : undefined;

function removeModelValue(field: keyof T, value: string) {
  const newModel = { ...model.value };

  if (newModel[field].value.length <= 1) {
    delete newModel[field];
  } else {
    newModel[field].value.splice(newModel[field].value.indexOf(value), 1);
  }

  model.value = newModel;
}

function toggleModelValue(field: keyof T, value: string) {
  if (getFilterActive(field, value)) {
    removeModelValue(field, value);
  } else {
    addModelValue(field, value);
  }
}

function toggleOverlay(event: Event) {
  if (!popover.value) {
    return;
  }

  popover.value.toggle(event);
}
</script>

<template>
  <div v-if="filters.length" class="-tw-m-1 tw-flex tw-flex-wrap tw-items-center md:tw-min-h-12">
    <VTransitionGroup duration="300" type="slideRight">
      <VDataTableFiltersButton
        v-for="{ field, value } of activeFilters"
        class="tw-m-1"
        :color="getValueColor(field, value)"
        :key="`${field}-${value}`"
        shadow
        :suffix-icon="['far', 'xmark']"
        @click="removeModelValue(field, value)"
      >
        {{ value }}
      </VDataTableFiltersButton>
    </VTransitionGroup>

    <VDataTableFiltersButton
      class="tw-m-1 tw-font-bold"
      :inverted="!!Object.keys(model).length"
      :prefix-icon="['far', 'plus']"
      :shadow="!Object.keys(model).length"
      @click="toggleOverlay"
    >
      Filter
    </VDataTableFiltersButton>

    <VPopover data-testid="data-table-filters__popover" ref="popover">
      <div class="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-4 tw-p-3">
        <div v-for="{ property, values } of filters" class="tw-grow" :key="property.field">
          <div class="tw-mb-2 tw-text-sm tw-text-primary-900" :level="2">
            {{ property.header }}
          </div>

          <div class="tw-grid tw-max-h-56 tw-gap-2 tw-overflow-auto tw-pr-2 tw-scrollbar-thin">
            <VDataTableFiltersButton
              v-for="value of values"
              :active="getFilterActive(property.field, value)"
              class="tw-font-bold"
              :color="getValueColor(property.field, value)"
              :key="value"
              @click="toggleModelValue(property.field, value)"
            >
              {{ value }}
            </VDataTableFiltersButton>
          </div>
        </div>
      </div>
    </VPopover>
  </div>
</template>
